import { notify } from '@billy/design-system'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { NotificationKeys } from '../../../enums/notificationKeys'
import { useImportedInvoiceDrawer } from '../context/importedInvoiceDrawer'

interface UseCloseUnresolvedImportedInvoiceDrawerProps {
  isLoadingError: boolean
  isNotFound: boolean
}

/**
 * An `importedInvoiceId` the backend cannot load — malformed, unknown (404) or failing — has
 * nothing to review, so the drawer leaves rather than describing an id the reader has no way to
 * act on. Lives in a hook because the drawer early-returns before its JSX, which rules out an
 * effect in the component body.
 *
 * Only a failed first load closes the drawer. A background refetch that fails while a cached
 * invoice is on screen leaves it open — the reader still has something to review.
 *
 * Runs once per failed id: `closeDrawer` drops the param, so the drawer stops rendering and the
 * effect cannot re-enter.
 */
export const useCloseUnresolvedImportedInvoiceDrawer = ({
  isLoadingError,
  isNotFound,
}: UseCloseUnresolvedImportedInvoiceDrawerProps): void => {
  const { t } = useTranslation()
  const { closeDrawer } = useImportedInvoiceDrawer()
  const isUnresolved = isLoadingError || isNotFound

  useEffect(() => {
    if (!isUnresolved) {
      return
    }

    notify({
      id: NotificationKeys.ImportedInvoiceReviewNotFound,
      message: t('imported_invoices.review.not_found.notification'),
      variant: 'error',
    })
    closeDrawer()
  }, [closeDrawer, isUnresolved, t])
}
